import { useState } from 'react';
import { Microphone, MicrophoneSlash } from '@phosphor-icons/react';
import { MicCircle } from './MicCircle';
import { recorderFallback, sendRecording, type Recording, type RecorderErrorKind } from '../hooks/recorder-state';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import { haptic, openTelegramLink } from '../lib/tma';
import { Button, Card, Spinner } from './ui';

/** Shorter recordings are taps by mistake: thrown away without a word. */
const MIN_RECORDING_MS = 1500;

export interface ThoughtRecorderProps {
  maxSeconds: number;
  /**
   * Called with the finished recording; a spinner shows until it settles. Resolves
   * true when it was accepted; otherwise the recording is kept to be sent again.
   */
  onRecorded: (blob: Blob, mimeType: string, durationMs: number) => Promise<boolean>;
  size?: 'lg' | 'sm';
  /** The bot chat for the fallback; the fallback button is hidden without it. */
  botUrl: string | null;
  /** Runs before the chat opens (e.g. tells the API what the next voice is for). */
  onChatFallback?: () => Promise<unknown>;
  /** The chat fallback failed. */
  onFailed?: (err?: unknown) => void;
  disabled?: boolean;
}

function mmss(ms: number): string {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Tap to start, then stop or cancel (thrown away); the chat when recording does not work here. */
export function ThoughtRecorder({
  maxSeconds,
  onRecorded,
  size = 'lg',
  botUrl,
  onChatFallback,
  onFailed,
  disabled = false,
}: ThoughtRecorderProps) {
  const t = useT();
  const [lastError, setLastError] = useState<RecorderErrorKind | null>(null);
  const [opening, setOpening] = useState(false);
  // A recording that did not go through: kept until it is sent again or thrown away.
  const [held, setHeld] = useState<Recording | null>(null);
  const [resending, setResending] = useState(false);

  const send = (r: Recording) => onRecorded(r.blob, r.mimeType, r.durationMs);
  const rec = useVoiceRecorder({
    maxDurationMs: maxSeconds * 1000,
    minDurationMs: MIN_RECORDING_MS,
    onResult: async (blob, mimeType, durationMs) => setHeld(await sendRecording(send, { blob, mimeType, durationMs })),
    onError: setLastError,
  });

  const fallback = recorderFallback(rec.state, lastError);

  const start = () => {
    setLastError(null);
    haptic('medium');
    void rec.start();
  };

  const toChat = async () => {
    if (!botUrl) return;
    setOpening(true);
    try {
      await onChatFallback?.();
      openTelegramLink(botUrl);
    } catch (err) {
      onFailed?.(err);
    } finally {
      setOpening(false);
    }
  };

  const resend = async () => {
    if (!held) return;
    setResending(true);
    const again = await sendRecording(send, held);
    setResending(false);
    setHeld(again);
  };

  if (held) {
    const buttonSize = size === 'lg' ? 'md' : 'sm';
    return (
      <div className={cn('flex flex-wrap items-center gap-2', size === 'lg' && 'flex-col py-2')}>
        <span className="text-sm text-hint">{t.recorder.notSent}</span>
        <div className="flex gap-2">
          <Button variant="secondary" size={buttonSize} disabled={resending} onClick={() => setHeld(null)}>
            {t.recorder.cancel}
          </Button>
          <Button variant="ink" size={buttonSize} loading={resending} onClick={resend}>
            {t.recorder.resend}
          </Button>
        </div>
      </div>
    );
  }

  const canRetry = fallback === 'denied' || fallback === 'failed';

  if (fallback !== 'none') {
    if (size === 'sm') {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs text-hint">
            <MicrophoneSlash size={14} />
            {t.recorder.unsupportedTitle}
          </span>
          {canRetry && (
            <Button variant="ghost" size="sm" onClick={start}>
              {t.common.retry}
            </Button>
          )}
          {botUrl && (
            <Button variant="secondary" size="sm" loading={opening} onClick={toChat}>
              {t.recorder.toChat}
            </Button>
          )}
        </div>
      );
    }
    return (
      <Card variant="sunken" padding="md" className="text-left">
        <p className="text-sm font-semibold mb-1">{t.recorder.unsupportedTitle}</p>
        <p className="text-sm text-hint mb-3 break-words">
          {fallback === 'denied'
            ? t.recorder.deniedText
            : fallback === 'failed'
              ? t.recorder.failedText
              : t.recorder.unsupportedText}
        </p>
        <div className="flex flex-wrap gap-2">
          {botUrl && (
            <Button variant="ink" size="md" loading={opening} onClick={toChat}>
              {t.recorder.toChat}
            </Button>
          )}
          {canRetry && (
            <Button variant="ghost" size="md" onClick={start}>
              {t.common.retry}
            </Button>
          )}
        </div>
      </Card>
    );
  }

  if (rec.state === 'pending') {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-hint', size === 'lg' && 'justify-center py-6')}>
        <Spinner size="sm" />
        {size === 'lg' && t.recorder.sending}
      </div>
    );
  }

  if (rec.state === 'recording') {
    return (
      <div className={cn('flex items-center gap-2', size === 'lg' ? 'flex-col gap-3' : 'flex-wrap')}>
        {size === 'lg' ? (
          <MicCircle recording label={t.recorder.stop} onClick={rec.stop} />
        ) : (
          <span className="inline-flex w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" aria-hidden />
        )}
        <span className="text-sm tabular-nums text-hint">{t.recorder.left(mmss(rec.remainingMs))}</span>
        <div className="flex gap-2">
          <Button variant="secondary" size={size === 'lg' ? 'md' : 'sm'} onClick={rec.cancel}>
            {t.recorder.cancel}
          </Button>
          <Button variant="ink" size={size === 'lg' ? 'md' : 'sm'} onClick={rec.stop}>
            {t.recorder.stop}
          </Button>
        </div>
      </div>
    );
  }

  if (size === 'sm') {
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled}
        aria-label={t.recorder.micLabel}
        className="inline-flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-full bg-ink text-ink-on transition-[transform,opacity] duration-150 active:scale-95 disabled:opacity-50"
      >
        <Microphone size={20} />
      </button>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <MicCircle label={t.recorder.micLabel} disabled={disabled} onClick={start} />
      <span className="text-sm text-hint">{t.recorder.start}</span>
    </div>
  );
}
